/**
 * Step geometry: where to scroll a target, what to highlight when it is taller
 * than the room left for the popover, and a validator that says whether a
 * rendered step is actually pointing at its target. Pure: callers pass rects.
 */

export interface Box {
  top: number
  left: number
  width: number
  height: number
}

export type Side = 'top' | 'bottom' | 'left' | 'right'

/** Room kept free for the popover (≈ its height + offset + arrow). */
// ponytail: fixed estimate; measure the rendered popover if copy ever gets much longer.
export const POPOVER_RESERVE_PX = 200
const EDGE_PX = 8
const TOLERANCE_PX = 4

export const box = (r: { top: number; left: number; width: number; height: number }): Box => ({
  top: r.top,
  left: r.left,
  width: r.width,
  height: r.height,
})
const bottom = (b: Box) => b.top + b.height
const right = (b: Box) => b.left + b.width

export function intersect(a: Box, b: Box): Box | null {
  const top = Math.max(a.top, b.top)
  const left = Math.max(a.left, b.left)
  const h = Math.min(bottom(a), bottom(b)) - top
  const w = Math.min(right(a), right(b)) - left
  return h > 0 && w > 0 ? { top, left, width: w, height: h } : null
}

const area = (b: Box | null) => (b ? b.width * b.height : 0)

/**
 * THE "can a person see it?" rule — shared by the anchored-vs-centred decision
 * (`pointable`) and the validator: at least min(50 % of the target, 40 % of
 * the region it shows in). A 3 px sliver at the screen edge is not visible.
 */
export const visibleEnough = (target: Box, region: Box) =>
  area(intersect(target, region)) >= Math.min(0.5 * area(target), 0.4 * area(region)) && area(target) > 0

const sideways = (side: Side | undefined) => side === 'left' || side === 'right'

/**
 * Too tall to highlight whole with the popover on its side. Beside it
 * (left/right) only the region's height matters; above/below, the popover's
 * room must fit too.
 */
export const isTall = (target: Box, region: Box, side?: Side, reserve = POPOVER_RESERVE_PX) =>
  target.height + (sideways(side) ? 0 : reserve) + EDGE_PX > region.height

/**
 * How far to scroll (positive = content moves up) so the target is in the
 * visible region with room for the popover on `side`; 0 when it already is.
 * A tall target gets its TOP shown.
 */
export function scrollDelta(target: Box, region: Box, side: Side | undefined, reserve = POPOVER_RESERVE_PX): number {
  const h = target.height
  const rh = region.height
  let desired: number
  if (isTall(target, region, side, reserve)) {
    desired = region.top + EDGE_PX
    return Math.abs(target.top - desired) <= TOLERANCE_PX ? 0 : target.top - desired
  }
  const inside = target.top >= region.top && bottom(target) <= bottom(region)
  const roomAbove = target.top - region.top >= reserve
  const roomBelow = bottom(region) - bottom(target) >= reserve
  if (side === 'top') {
    if (inside && roomAbove) return 0
    desired = region.top + reserve
  } else if (side === 'bottom') {
    if (inside && roomBelow) return 0
    desired = region.top + Math.max(EDGE_PX, Math.min((rh - h) / 2, rh - h - reserve))
  } else {
    if (inside) return 0
    desired = region.top + (rh - h) / 2
  }
  return target.top - desired
}

/**
 * What a tall target highlights, and where the popover goes:
 * - beside it (left/right): its whole visible part, popover on that side;
 * - its top shown (usual): the visible top, popover under it;
 * - scroller can't bring the top up (page end) and room above: the whole
 *   visible part, popover above.
 */
export function visiblePart(
  target: Box,
  region: Box,
  side?: Side,
  reserve = POPOVER_RESERVE_PX,
): { part: Box; side: Side } | null {
  const seen = intersect(target, region)
  if (!seen) return null
  if (side === 'left' || side === 'right') return { part: seen, side }
  if (seen.top - region.top >= reserve) return { part: seen, side: 'top' }
  const room: Box = { ...region, height: Math.max(region.height - reserve, 0) }
  const part = intersect(target, room)
  return part ? { part, side: 'bottom' } : null
}

/** driver.js draws the stage as the 2nd sub-path of its overlay SVG: `M x+r,y h w a… v h a… …`. */
export function parseStagePath(d: string | null | undefined): Box | null {
  const sub = d?.split(/\s*(?=M)/).filter(Boolean)[1]
  if (!sub) return null
  const m = /M\s*(-?[\d.]+)[ ,](-?[\d.]+)\s*h\s*(-?[\d.]+)\s*a\s*(-?[\d.]+)[ ,][^v]*v\s*(-?[\d.]+)/.exec(sub)
  if (!m) return null
  const [x, y, w, r, h] = m.slice(1).map(Number)
  return { top: y, left: x - r, width: w + 2 * r, height: h + 2 * r }
}

export interface GeometryInput {
  /** The step's real target (null for a centred step). */
  target: Box | null
  /** What the stage should frame: the target, or its visible part for a tall one. */
  highlight: Box | null
  stage: Box | null
  popover: Box | null
  /** The side driver.js placed the popover on (`driver-popover-side-*`); 'over' / unknown skips the relation checks. */
  popoverSide?: Side | 'over' | null
  viewport: { width: number; height: number }
  /** Where the target can actually be seen (its scroll container ∩ viewport); defaults to the viewport. */
  visibleRegion?: Box | null
  /** Px the engine left scrolled on axes the user can't scroll back (overflow hidden/clip). */
  lockedScrollOffset?: number
  stagePadding: number
}

export type GeometryError =
  | 'stage-missing'
  | 'stage-mismatch'
  | 'target-not-visible'
  | 'popover-missing'
  | 'popover-offscreen'
  | 'popover-overlaps-target'
  | 'popover-detached'
  | 'arrow-not-facing-target'
  | 'locked-scroll-offset'

/** Popover edge to highlight edge: stage padding + popover offset + arrow, with slack. */
const MAX_GAP_PX = 48

/** Would a person see the right thing? Empty list = yes. */
export function validateStepGeometry(g: GeometryInput): GeometryError[] {
  const errors: GeometryError[] = []
  const vp: Box = { top: 0, left: 0, width: g.viewport.width, height: g.viewport.height }
  if ((g.lockedScrollOffset ?? 0) > 1) errors.push('locked-scroll-offset')
  if (!g.popover) errors.push('popover-missing')
  else if (
    g.popover.top < -TOLERANCE_PX ||
    g.popover.left < -TOLERANCE_PX ||
    bottom(g.popover) > vp.height + TOLERANCE_PX ||
    right(g.popover) > vp.width + TOLERANCE_PX
  )
    errors.push('popover-offscreen')
  if (!g.target || !g.highlight) return errors // centred step: popover checks only

  const p = g.stagePadding
  if (!g.stage) errors.push('stage-missing')
  else {
    const want = {
      top: g.highlight.top - p,
      left: g.highlight.left - p,
      width: g.highlight.width + 2 * p,
      height: g.highlight.height + 2 * p,
    }
    const off = Math.max(
      Math.abs(g.stage.top - want.top),
      Math.abs(g.stage.left - want.left),
      Math.abs(bottom(g.stage) - bottom(want)),
      Math.abs(right(g.stage) - right(want)),
    )
    if (off > TOLERANCE_PX) errors.push('stage-mismatch')
  }
  const region = g.visibleRegion ?? vp
  // A tall target counts as seen when the part the step frames is.
  const seen = visibleEnough(g.target, region) || (g.highlight !== g.target && visibleEnough(g.highlight, region))
  if (!seen) errors.push('target-not-visible')
  const shown = intersect(g.highlight, vp)
  if (g.popover && shown) {
    const overlap = intersect(g.popover, shown)
    if (overlap && overlap.width > TOLERANCE_PX && overlap.height > TOLERANCE_PX) errors.push('popover-overlaps-target')
  }
  if (g.popover && g.popoverSide && g.popoverSide !== 'over') {
    const h = g.highlight
    const gap = {
      top: h.top - bottom(g.popover),
      bottom: g.popover.top - bottom(h),
      left: h.left - right(g.popover),
      right: g.popover.left - right(h),
    }[g.popoverSide]
    if (gap < -TOLERANCE_PX || gap > MAX_GAP_PX) errors.push('popover-detached')
    // The arrow sits on the popover edge facing the target: their cross-axis spans must meet.
    const crossMeets = sideways(g.popoverSide)
      ? Math.min(bottom(g.popover), bottom(h)) > Math.max(g.popover.top, h.top)
      : Math.min(right(g.popover), right(h)) > Math.max(g.popover.left, h.left)
    if (!crossMeets) errors.push('arrow-not-facing-target')
  }
  return errors
}

// ── DOM side (thin; the decisions above are the tested part) ──

/** jsdom / display:none elements have no box: nothing to scroll or measure. */
export const laidOut = (el: Element) => {
  const r = el.getBoundingClientRect()
  return r.width > 0 || r.height > 0
}

/**
 * The element that actually scrolls `el` (the app scrolls inside a layout
 * container, not the window). driver.js locks the active step's scroll parent
 * with `overflow: hidden !important` (`driver-active-element-parent-no-scroll`);
 * that container still scrolls programmatically and is still the one to use.
 */
export function scrollParent(el: Element): Element {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p)
    const scrolls =
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay' ||
      p.classList.contains('driver-active-element-parent-no-scroll')
    if (scrolls && p.scrollHeight > p.clientHeight) return p
  }
  return document.scrollingElement ?? document.documentElement
}

/** The part of the window a scroller shows. */
export function regionOf(scroller: Element): Box {
  const vp: Box = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
  if (scroller === document.scrollingElement || scroller === document.documentElement) return vp
  return intersect(box(scroller.getBoundingClientRect()), vp) ?? vp
}

export function scrollInstantly(scroller: Element, delta: number): void {
  // 'instant' also beats a CSS `scroll-behavior: smooth` on the container.
  if (typeof scroller.scrollBy === 'function') scroller.scrollBy({ top: delta, behavior: 'instant' as ScrollBehavior })
  else scroller.scrollTop += delta
}

/** Resolves once `el`'s rect is unchanged for 2 consecutive frames (or after `maxMs`). */
export function whenStable(el: Element, maxMs = 400): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now()
    let last = ''
    let same = 0
    const tick = () => {
      const r = el.getBoundingClientRect()
      const key = `${r.top},${r.left},${r.width},${r.height}`
      same = key === last ? same + 1 : 0
      last = key
      if (same >= 2 || performance.now() - started > maxMs) resolve()
      else requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })
}

const clips = (v: string) => v !== 'visible'
const userScrollable = (v: string) => v === 'auto' || v === 'scroll' || v === 'overlay'

/** The window ∩ every ancestor box that clips `el` (overflow other than visible). */
export function clipRegion(el: Element): Box {
  let region: Box = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const cs = getComputedStyle(p)
    if (!clips(cs.overflowX) && !clips(cs.overflowY)) continue
    const r = box(p.getBoundingClientRect())
    const next = intersect(region, {
      top: clips(cs.overflowY) ? r.top : region.top,
      left: clips(cs.overflowX) ? r.left : region.left,
      width: clips(cs.overflowX) ? r.width : region.width,
      height: clips(cs.overflowY) ? r.height : region.height,
    })
    if (!next) return { top: 0, left: 0, width: 0, height: 0 }
    region = next
  }
  return region
}

/**
 * Can the tour point at it? Laid out, and `visibleEnough` SIDEWAYS in what
 * clips it (vertically the engine scrolls it into view, so its height is
 * projected into the region first). Same predicate the validator uses.
 */
export function pointable(el: Element): boolean {
  if (!laidOut(el)) return false
  const t = box(el.getBoundingClientRect())
  const region = clipRegion(el)
  if (region.width <= 0) return false
  const projected: Box = { ...t, top: region.top, height: Math.min(t.height, Math.max(region.height, 1)) }
  return visibleEnough(projected, { ...region, height: Math.max(region.height, 1) })
}

/** Px left scrolled on an axis the user can't scroll back (hidden/clip, not driver's temporary lock). */
export function lockedScrollOffset(el: Element): number {
  let total = 0
  for (let p = el.parentElement; p; p = p.parentElement) {
    const cs = getComputedStyle(p)
    if (!userScrollable(cs.overflowX) && p !== document.scrollingElement) total += Math.abs(p.scrollLeft)
    const locked = p.classList.contains('driver-active-element-parent-no-scroll')
    if (!userScrollable(cs.overflowY) && !locked && p !== document.scrollingElement) total += Math.abs(p.scrollTop)
  }
  return total
}
